import { useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import { selectIsEditMode } from 'features/common/editModeSlice';
import counterPatternService from 'features/CounterPattern/counterPatternService';
import { CouterPatternStoreMODisplay } from 'features/CounterPattern/models';
import { COUPON_CLASSIFICATIONS } from 'features/MenuList/constants';
import MobileOrderDisplayModal, {
  MobileOrderDisplayModalProps,
} from 'features/Store/components/MobileOrderDisplayModal';
import { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { displayError, handleServiceError } from 'utils';
import CounterPatternStoreTable from './CounterPatternStoreTable';

export interface CounterPatternStoreMobileOrderDisplayModalProps {
  posCodeId: number;
  posCode: string;
  posName: string;
  menuName: string;
  menuCode: string;
  couponClassification: number;
  dataDate: Date;
  onClose?: (isDataChanged?: boolean) => void;
}

function CounterPatternStoreMobileOrderDisplayModal({
  posCodeId,
  posCode,
  posName,
  menuName,
  menuCode,
  couponClassification,
  dataDate,
  onClose,
}: CounterPatternStoreMobileOrderDisplayModalProps) {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  // states
  const [storeList, setStoreList] = useState<CouterPatternStoreMODisplay[]>([]);
  const [showMobileOrderDisplayModal, setShowMobileOrderDisplayModal] = useState(false);
  const [mobileOrderDisplayModalData, setMobileOrderDisplayModalData] = useState<MobileOrderDisplayModalProps>();
  const [isDataChanged, setIsDataChanged] = useState(false);

  // events
  function handleClose() {
    onClose?.(isDataChanged);
  }

  function loadDataList() {
    (async () => {
      try {
        const response = await counterPatternService.getCounterPatternStoreMobileOrderDisplays(posCodeId, dataDate);
        setStoreList(response.data);
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    })();
  }

  function handleCloseMobileOrderDisplayModal(isDataMofified: boolean = false) {
    if (isDataMofified) {
      loadDataList();
      setIsDataChanged(true);
    }

    setShowMobileOrderDisplayModal(false);
    setMobileOrderDisplayModalData(undefined);
  }

  function handleOpenMobileOrderDisplayModal(
    restaurantId: number,
    restaurantName: string,
    restaurantCode: string,
    mobileOrderDisplayFlg: boolean
  ) {
    setMobileOrderDisplayModalData({
      posCodeId: posCodeId,
      posCode: posCode,
      restaurantId: restaurantId,
      restaurantCode: restaurantCode,
      restaurantName: restaurantName,
      menuName: menuName,
      menuCode: menuCode,
      couponClassification: couponClassification,
      defaultMobileOrderDisplayFlg: mobileOrderDisplayFlg,
    });
    setShowMobileOrderDisplayModal(true);
  }

  // side effects
  useEffect(() => {
    loadDataList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal show size="lg" backdrop="static" onHide={handleClose} centered>
      <Modal.Header closeButton className={isEditMode ? 'reedit-mode-wrapper' : ''}>
        <FeatureTitleBar title={t('CounterPatternStoreMobileOrderDisplayModal.head_title')}></FeatureTitleBar>
      </Modal.Header>
      <Modal.Body className={`pb-3 ${isEditMode ? 'reedit-mode-wrapper' : ''}`}>
        <div className="mb-3">
          {t('CounterPatternStoreMobileOrderDisplayModal.menu')}：&nbsp;{menuName}&nbsp;(
          {menuCode + (COUPON_CLASSIFICATIONS[couponClassification] || '')})
        </div>
        <div className="fw-bold mb-3 fs-5">
          POS：&nbsp;&nbsp;{posName}&nbsp;&nbsp;({posCode})
        </div>
        <div className="d-flex mb-1 justify-content-end align-items-center">
          <div className="border-individual-setting d-inline-block me-3"></div>
          <span>{t('CounterPatternStoreMobileOrderDisplayModal.setting_mo_guide')}</span>
        </div>
        <div className="sticky-table flex-grow-1 flex-shrink-1" style={{ maxHeight: '480px' }}>
          <CounterPatternStoreTable
            data={storeList}
            onOpenMobileOrderDisplayModal={handleOpenMobileOrderDisplayModal}
          />
        </div>

        {showMobileOrderDisplayModal &&
          mobileOrderDisplayModalData &&
          createPortal(
            <MobileOrderDisplayModal
              posCodeId={mobileOrderDisplayModalData.posCodeId}
              posCode={mobileOrderDisplayModalData.posCode}
              restaurantId={mobileOrderDisplayModalData.restaurantId}
              restaurantName={mobileOrderDisplayModalData.restaurantName}
              restaurantCode={mobileOrderDisplayModalData.restaurantCode}
              menuName={mobileOrderDisplayModalData.menuName}
              menuCode={mobileOrderDisplayModalData.menuCode}
              couponClassification={mobileOrderDisplayModalData.couponClassification}
              defaultMobileOrderDisplayFlg={mobileOrderDisplayModalData.defaultMobileOrderDisplayFlg}
              onClose={handleCloseMobileOrderDisplayModal}
            />,
            document.body
          )}
      </Modal.Body>
    </Modal>
  );
}

export default CounterPatternStoreMobileOrderDisplayModal;
